import type { AppState, ThunkAction, Dispatch } from '../../types/app.types';

export const Devices = {
  mobile: 0b001,
  tablet: 0b010,
  desktop: 0b100,
};
export type SearchView =
  | 'MEDICATION_SEARCH'
  | 'PHARMACY_SEARCH'
  | 'PROVIDER_SEARCH'
  | 'ALLERGY_SEARCH'
  | 'OTHER_MEDICATION_SEARCH'
  | 'MEDICAL_CONDITION_SEARCH';
export type HeaderView = 'DEFAULT' | 'TAKE_OVER' | 'PURCHASE';
export type HeaderState = {
  device: number;
  readonly view: HeaderView | SearchView;
  readonly title: string | null | undefined;
  history: {
    view: HeaderView | SearchView | null | undefined;
    title: string | null | undefined;
  };
  currPath: string;
  currPathState: Record<string, any>;
  prevPath: string;
  prevPathState: Record<string, any>;
  isGSAoniOS: boolean | null | undefined;
  os: string;
};
const initialState: HeaderState = {
  device: Devices.desktop,
  view: 'DEFAULT',
  title: null,
  history: {
    view: null,
    title: null,
  },
  currPath: '',
  currPathState: {},
  prevPath: '',
  prevPathState: {},
  isGSAoniOS: false,
  os: '',
};
export type SetOS = {
  type: 'header/SET_OS';
  os: string;
};

export function setOS(os: string): SetOS {
  return {
    type: 'header/SET_OS',
    os,
  };
}

export type SetDevice = {
  type: 'header/SET_DEVICE';
  device: number;
};

export function setDevice(device: number): SetDevice {
  return {
    type: 'header/SET_DEVICE',
    device,
  };
}

export type SetIsGSAoniOS = {
  type: 'header/SET_IS_GSA_ON_IOS';
  isGSAoniOS: boolean;
};

export function setIsGSAoniOS(isGSAoniOS: boolean): SetIsGSAoniOS {
  return {
    type: 'header/SET_IS_GSA_ON_IOS',
    isGSAoniOS,
  };
}

export type UpdateHeader = {
  type: 'header/UPDATE';
  path: string;
  state: Record<string, any> | null | undefined;
};

export function updateHeader(path: string, state?: Record<string, any>): UpdateHeader {
  return {
    type: 'header/UPDATE',
    path,
    state,
  };
}

export type SetView = {
  type: 'header/SET_VIEW';
  view: HeaderView | SearchView;
  title?: string;
  itemIds?: Array<string>;
};
export const setSearchView = (searchView?: SearchView): SetView => ({
  type: 'header/SET_VIEW',
  view: searchView || 'MEDICATION_SEARCH',
});
export const setTakeOverView = (title: string): SetView => ({
  type: 'header/SET_VIEW',
  view: 'TAKE_OVER',
  title,
});
export const setDefaultView = (): SetView => ({
  type: 'header/SET_VIEW',
  view: 'DEFAULT',
});
export const setPurchaseView = (): SetView => ({
  type: 'header/SET_VIEW',
  view: 'PURCHASE',
});

export function revertHeader(): ThunkAction<SetView> {
  return function (dispatch: Dispatch, getState): SetView {
    const state = getState().header;
    return dispatch({
      type: 'header/SET_VIEW',
      view: state.history.view,
      title: state.history.title,
    });
  };
}

const accountRootPathRegexp = /^\/account\/\w+$/;
const medDetailsRegexp = /(\/account\/medications\/)\d+$/;

export function closeTakeOver(): ThunkAction {
  return function (dispatch: Dispatch, getState, { history }): void {
    const location = history && history.location;
    const pathname = location && location.pathname;
    const { from, backToPrev } = (location && location.state) || {};

    if (backToPrev) {
      history.goBack();
    }

    if (['/quicksave/prescriptions', '/quicksave/verify', '/quicksave/otp/register', '/login/otp'].includes(from)) {
      history.push('/account');
    } else if (pathname === '/order-service/account/my-meds') {
      history.push('/order-service/account');
    } else if (medDetailsRegexp.test(history && pathname)) {
      history.push({
        pathname: '/account/medications',
        state: {
          fromDetailsView: true,
        },
      });
    } else if (accountRootPathRegexp.test(pathname)) {
      history.push('/account');
    } else {
      history.goBack();
    }
  };
}

export type HeaderAction = SetDevice | SetIsGSAoniOS | UpdateHeader | SetView | SetOS;
export default function (state: HeaderState = initialState, action: HeaderAction): HeaderState {
  switch (action.type) {
    case 'header/SET_OS':
      return { ...state, os: action.os };

    case 'header/SET_DEVICE':
      return { ...state, device: action.device };

    case 'header/SET_IS_GSA_ON_IOS':
      return { ...state, isGSAoniOS: action.isGSAoniOS };

    case 'header/UPDATE':
      return {
        ...state,
        currPath: action.path || '',
        currPathState: action.state || {},
        prevPath: state.currPath || '',
        prevPathState: state.currPathState || {},
      };

    case 'header/SET_VIEW':
      return {
        ...state,
        view: action.view,
        title: action.title,
        history: {
          view: state.view,
          title: state.title,
        },
      };

    default:
      return state;
  }
}

export function isMobile(state: AppState): boolean {
  return state.header.device === Devices.mobile;
}

export function isTablet(state: AppState): boolean {
  return state.header.device === Devices.tablet;
}

export function isDesktop(state: AppState): boolean {
  return state.header.device === Devices.desktop;
}

export const isSearchView = (state: AppState): boolean =>
  [
    'MEDICATION_SEARCH',
    'PHARMACY_SEARCH',
    'PROVIDER_SEARCH',
    'ALLERGY_SEARCH',
    'OTHER_MEDICATION_SEARCH',
    'MEDICAL_CONDITION_SEARCH',
  ].includes(state.header.view);

export const isDefaultView = (state: AppState): boolean => state.header.view === 'DEFAULT';
export const isTakeOverView = (state: AppState): boolean => state.header.view === 'TAKE_OVER';
export const isPurchaseView = (state: AppState): boolean => state.header.view === 'PURCHASE';
export const isHomepage = (state: AppState): boolean => state.header.currPath === '/';
